import Redactor from '../../redactor.esm.min.js'
import { registerToolbarButton, setToolbarButtonHtml } from '../registerButton'
import { currentBlockElements, stripThemeOverrides } from '../textStyle'

class MpTextFormatPlugin {
    static defaultOptions = {
        input: true
    }

    static defaultLang = {
        en: {
            mpTextFormat: {
                title: 'Paragraph'
            }
        }
    }

    constructor(app, options = {}) {
        this.app = app
        this.options = options
        const buttonsObj = app.getOption('buttonsObj') || {}
        this.excludedFormats = options.excludedFormats || []
        this.formats = [
            { label: 'Paragraph', value: 'p', icon: buttonsObj.text?.icon || '' },
            { label: 'Heading 1', value: 'h1', icon: buttonsObj.h1?.icon || '' },
            { label: 'Heading 2', value: 'h2', icon: buttonsObj.h2?.icon || '' },
            { label: 'Heading 3', value: 'h3', icon: buttonsObj.h3?.icon || '' },
            { label: 'Heading 4', value: 'h4', icon: buttonsObj.h4?.icon || '' }
        ]
        if (this.excludedFormats.length > 0) {
            this.formats = this.formats.filter((format) => !this.excludedFormats.includes(format.value))
        }
        this.currentlySelectedFormat = ''
        this.defaultTitle = 'Text Format'
        this.buttonDropDownIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                <path d="M4.5 6L8.5 10L12.5 6" stroke="#9B9FA6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`
        this._onSelectionChange = () => {
            setTimeout(() => this._updateButtonContent(), 10)
        }
    }

    start() {
        registerToolbarButton(this.app, 'mpTextFormat', this._createToolbarButton())
        this.app.on('click', this._onSelectionChange)
        this.app.on('key:up', this._onSelectionChange)
        this.app.on('editor:focus', this._onSelectionChange)
    }

    stop() {
        this.app.off('click', this._onSelectionChange)
        this.app.off('key:up', this._onSelectionChange)
        this.app.off('editor:focus', this._onSelectionChange)
    }

    _createToolbarButton() {
        return {
            title: 'Text Format',
            color: false,
            html: `<div>${this.defaultTitle}</div>
            ${this.buttonDropDownIcon}
            `,
            classname: 'mp-redactor-toolbar-plugin mp-text-format-button',
            position: { before: 'moreinline' },
            command: 'mpTextFormat.popup'
        }
    }

    _updateButtonContent() {
        const instance = this.app.block.get()
        let selectedFormat = this.defaultTitle
        this.currentlySelectedFormat = ''
        if (instance && instance.getTag) {
            const blockTag = instance.getTag()
            const foundFormat = this.formats.find((format) => format.value === blockTag)
            if (foundFormat) {
                selectedFormat = foundFormat.label
                this.currentlySelectedFormat = foundFormat.value
            }
        }

        setToolbarButtonHtml(this.app, 'mpTextFormat', `<div>${selectedFormat}</div>${this.buttonDropDownIcon}`)
    }

    popup(_params, { button, el } = {}) {
        this.app.saveSelection()

        const items = this.formats.map((format) => ({
            title:
                "<span class='mpTextFormat-list-item' style='padding: 0; margin: 0;'>" +
                format.icon +
                ' ' +
                format.label +
                '</span>',
            command: 'mpTextFormat.set',
            active: this.currentlySelectedFormat === format.value,
            params: {
                tag: format.value,
                name: format.value
            }
        }))
        this.app.dropdown.create('mpTextFormat', { items: items })
        this.app.dropdown.open(button || el)
    }

    set(params = {}) {
        this.app.dropdown.close()
        const tag = String(params.tag || '').toLowerCase()
        const before = currentBlockElements(this.app)
        // format.set turns a block back into a paragraph when it is already that
        // heading. Skip that toggle and just clear the inline styles hiding it.
        const already = tag && before.length > 0 && before.every((el) => el.tagName.toLowerCase() === tag)
        let formatted = []
        if (!already) {
            if (tag === 'p') {
                formatted = this.app.format.set({ type: 'text' }) || []
            } else {
                const level = Number(tag.replace(/^h/, ''))
                if (level) formatted = this.app.format.set({ type: 'heading', headingLevel: level }) || []
            }
        }
        this._stripThemeOverrides(formatted)
    }

    _stripThemeOverrides(formatted) {
        const seen = new Set()
        const add = (el) => {
            if (!el || el.nodeType !== 1 || !el.isConnected || seen.has(el)) return
            seen.add(el)
            stripThemeOverrides(el)
        }
        ;(formatted || []).forEach((block) => {
            if (block && block.getEl) add(block.getEl())
        })
        currentBlockElements(this.app).forEach(add)
    }
}

Redactor.addPlugin('mpTextFormat', MpTextFormatPlugin)
